import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Link as MuiLink,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { ContractStatusBadge } from '@/components/contracts/ContractStatusBadge'
import { ROUTES } from '@/utils/constants'
import type { User } from '@/types'

interface UserCardProps {
  user: User
  onView: (user: User) => void
  onDelete: (user: User) => void
}

export function UserCard({ user, onView, onDelete }: UserCardProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleView = () => {
    handleMenuClose()
    onView(user)
  }

  const handleDelete = () => {
    handleMenuClose()
    onDelete(user)
  }

  const displayName =
    user.display_name ||
    (user.first_name && user.surname
      ? `${user.surname} ${user.first_name}`
      : user.email)

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              mr: 2,
              width: 48,
              height: 48,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <MuiLink
                component={RouterLink}
                to={`${ROUTES.USERS}/${user.id}`}
                underline="hover"
                sx={{ fontWeight: 600, fontSize: 16 }}
              >
                {displayName}
              </MuiLink>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {user.email}
            </Typography>
            {user.contract?.group && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <MuiLink
                  component={RouterLink}
                  to={`${ROUTES.GROUPS}/${user.contract.group.id}`}
                  underline="hover"
                  sx={{ fontSize: 14 }}
                >
                  {user.contract.group.name}
                </MuiLink>
              </Typography>
            )}
            <Box sx={{ mt: 1 }}>
              {user.contract ? (
                <ContractStatusBadge
                  status={user.contract.status}
                  label={user.contract.status_label}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('users.noContract')}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={handleMenuOpen} sx={{ ml: 'auto' }}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.view')}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          {t('users.delete')}
        </MenuItem>
      </Menu>
    </Card>
  )
}
